import React, { useState, useContext, useEffect, useCallback } from 'react';
import { doc, updateDoc, getDoc, collection, query, where, getDocs, setDoc, addDoc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import TourContext from '../../context/TourContext';
import UserContext from '../../context/UserContext';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMapMarkerAlt, 
  faCar, 
  faPhone, 
  faUser,
  faUserGroup,
  faMusic,
  faFileAlt,
  faEdit,
  faChevronRight,
  faUsers,
  faPlus,
  faSearch,
  faTimes,
  faTrash,
  faSuitcase,
  faClipboardCheck,
  faEye
} from '@fortawesome/free-solid-svg-icons';
import { liteClient } from 'algoliasearch/lite';
import styles from './Event.module.css';
import defaultVenueImage from '../../assets/Images/defaulthotelpicture.png';
import defaultUserImage from '../../assets/Images/defaultUserProfilePicture.png';
import verifiedBadge from '../../assets/Images/profileverifiedtick.png';
import olaLogo from '../../assets/Images/OLAlogo.png';
import uberLogo from '../../assets/Images/Uberlogo.png';
import rapidoLogo from '../../assets/Images/Rapidologo.jpg';
import { createNotification, sendEventInvitationNotification } from '../../utils/NotificationService';
import { useNavigate, useLocation } from 'react-router-dom';

// Initialize Algolia
const client = liteClient("6DJNAZSX4P", "342932cbe26f7751ec87fa2b1ff32e4c");

const Event = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // State variables
  const [isVendorModalVisible, setIsVendorModalVisible] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [isRideModalVisible, setIsRideModalVisible] = useState(false);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [isOrganizerModalVisible, setIsOrganizerModalVisible] = useState(false);
  const [organizerName, setOrganizerName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [techRiderCount, setTechRiderCount] = useState(0);
  
  // Vendor search states
  const [vendorSearchQuery, setVendorSearchQuery] = useState('');
  const [vendorResults, setVendorResults] = useState([]);
  const [searchingVendors, setSearchingVendors] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Context variables
  const { 
    selectedEvent, 
    getSelectedDate, 
    updateDate 
  } = useContext(TourContext);
  const { userData } = useContext(UserContext);
  
  // Get current event date
  const eventDate = getSelectedDate();
  const event = eventDate?.event || {};
  const venueData = event?.venueDetails?.venueData || {};
  
  // Determine event type
  const eventType = selectedEvent?.type || 'tour';
  
  // Define variables for user type check
  const isVendorOrStaff = userData?.userType === 'Vendor' || userData?.userType === 'Staff';
  
  // Check if user is authorized to remove vendors
  const canRemoveVendor = userData?.userType === 'Manager' || 
                          selectedEvent?.createdBy?.userId === userData?.userId;

  // Load tech rider count
  useEffect(() => {
    if (eventDate?.event?.techrider?.techRiders) {
      setTechRiderCount(eventDate.event.techrider.techRiders.length);
    } else {
      setTechRiderCount(0);
    }
  }, [eventDate]);

  // Initialize organizer name from event date data
  useEffect(() => {
    if (eventDate?.event?.organizer?.organizerName) {
      setOrganizerName(eventDate.event.organizer.organizerName);
    } else {
      setOrganizerName('');
    }
  }, [eventDate]);

  // Load vendor details from both assignedUsers AND pendingInvitations subcollections
  useEffect(() => {
    const loadVendors = async () => {
      if (!selectedEvent?.id || !eventDate?.id) return;

      try {
        const db = getFirestoreInstance();
        const collectionName = eventType === 'tour' ? 'tours' : 'shows';
        const allVendors = [];
        
        // 1. First, get vendors from assignedUsers subcollection (accepted vendors)
        const assignedUsersQuery = query(
          collection(db, collectionName, selectedEvent.id, 'assignedUsers'),
          where('role', '==', 'vendor')
        );
        
        const assignedUsersSnapshot = await getDocs(assignedUsersQuery);
        
        for (const userDoc of assignedUsersSnapshot.docs) {
          const userData = userDoc.data();
          
          // Check if vendor is assigned to this date
          const isAssignedToDate = userData.assignedDates?.some(
            date => date.dateDocId === eventDate.id
          );
          
          if (isAssignedToDate) {
            allVendors.push({
              userId: userData.userId,
              fullName: userData.fullName || '',
              userName: userData.userName || '',
              profilePictureUrl: userData.profilePictureUrl || '',
              userSubType: userData.userSubType || '',
              email: userData.email || '',
              idVerificationStatus: userData.idVerificationStatus || '',
              status: 'accepted'
            });
          }
        }
        
        // 2. Then, get vendors from pendingInvitations subcollection (pending vendors)
        const pendingInvitationsQuery = query(
          collection(db, collectionName, selectedEvent.id, 'pendingInvitations'),
          where('userType', '==', 'Vendor')
        );
        
        const pendingInvitationsSnapshot = await getDocs(pendingInvitationsQuery);
        
        for (const invitationDoc of pendingInvitationsSnapshot.docs) {
          const invitationData = invitationDoc.data();
          
          // Check if invitation is for this date
          const isForThisDate = invitationData.assignedDates?.some(
            date => date.dateDocId === eventDate.id
          );
          
          if (isForThisDate && invitationData.status !== 'rejected') {
            // Add to vendors list if not already added (avoid duplicates)
            if (!allVendors.some(v => v.userId === invitationData.userId)) {
              allVendors.push({
                userId: invitationData.userId,
                fullName: invitationData.fullName || '',
                userName: invitationData.userName || '',
                profilePictureUrl: invitationData.profilePictureUrl || '',
                userSubType: invitationData.userSubType || '',
                email: invitationData.email || '',
                idVerificationStatus: invitationData.idVerificationStatus || '',
                status: 'pending'
              });
            }
          }
        }
        
        setVendors(allVendors);
      } catch (error) {
        console.error('Error loading vendors:', error);
        setVendors([]);
      }
    };
    
    loadVendors();
  }, [selectedEvent, eventDate, eventType]);

  // Save organizer name
  const handleSaveOrganizerName = async () => {
    if (!selectedEvent?.id || !eventDate?.id) return;
    
    setIsSaving(true);
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      const eventDateRef = doc(db, collectionName, selectedEvent.id, 'eventDates', eventDate.id);

      await updateDoc(eventDateRef, {
        'event.organizer.organizerName': organizerName
      });

      // Let the listener handle the state update
      await updateDate(selectedEvent.id, eventDate.id, eventType);
      setIsOrganizerModalVisible(false);
    } catch (error) {
      console.error('Error saving organizer name:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Navigation functions
  const handleNavigateToChecklist = () => {
    navigate(`/${eventType}/${selectedEvent?.id}/${eventDate?.id}/checklist`, {
      state: { 
        previousPath: location.pathname,
        fromEvent: true
      }
    });
  };

  const handleNavigateToGuestList = () => {
    navigate(`/${eventType}/${selectedEvent?.id}/${eventDate?.id}/guestlist`);
  };

  const handleNavigateToSetlist = () => {
    navigate(`/${eventType}/${selectedEvent?.id}/${eventDate?.id}/setlist`);
  };

  const handleNavigateToTechRider = () => {
    navigate(`/${eventType}/${selectedEvent?.id}/${eventDate?.id}/techrider`, {
      state: { 
        previousPath: location.pathname,
        fromEvent: true
      }
    });
  };

  // Vendor search functions
  const handleVendorSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleVendorSearch();
    }
  };

  const handleVendorSearch = async () => {
    if (!vendorSearchQuery.trim()) return;
    
    try {
      setSearchingVendors(true);
      const { results } = await client.search([{
        indexName: 'users_index',
        query: vendorSearchQuery,
        filters: 'userType:Vendor',
        hitsPerPage: 10
      }]);
  
      const formattedVendors = results[0].hits.map(hit => ({
        id: hit.objectID,
        fullName: hit.fullName,
        email: hit.email,
        userName: hit.userName,
        userSubType: hit.userSubType,
        profilePictureUrl: hit.profilePictureUrl,
        userType: hit.userType,
        idVerificationStatus: hit.idVerificationStatus,
        isAlreadyAdded: vendors.some(vendor => vendor.userId === hit.objectID)
      }));
      
      setVendorResults(formattedVendors);
    } catch (error) {
      console.error('Error searching vendors:', error);
      setVendorResults([]);
    } finally {
      setSearchingVendors(false);
    }
  };

  // Add vendor to event - CORRECTED IMPLEMENTATION
  const handleAddVendor = async (vendor) => {
    if (!vendor || vendor.isAlreadyAdded) return;
    
    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Create a unique invitation ID
      const invitationId = Date.now().toString();
      
      // Prepare invitation data
      const invitationData = {
        invitationId: invitationId,
        eventId: selectedEvent.id,
        eventType: eventType,
        tourId: eventType === 'tour' ? selectedEvent.id : null,
        showId: eventType === 'show' ? selectedEvent.id : null,
        tourName: selectedEvent.tourName || '',
        showName: eventType === 'show' ? (selectedEvent.showName || selectedEvent.tourName) : '',
        actName: selectedEvent.actName || '',
        tourCoverPhotoUrl: selectedEvent.tourCoverPhotoUrl || '',
        
        // Venue information
        venueName: venueData?.displayName || 'Venue',
        venueAddress: venueData?.formattedAddress || '',
        
        // User details
        userId: vendor.id,
        userName: vendor.userName || '',
        fullName: vendor.fullName || '',
        email: vendor.email || '',
        profilePictureUrl: vendor.profilePictureUrl || '',
        userType: 'Vendor',
        userSubType: vendor.userSubType || '',
        role: 'vendor',
        
        // Date information
        assignedDates: [{
          dateDocId: eventDate.id,
          date: eventDate.date
        }],
        
        // Status information
        status: 'pending',
        createdAt: serverTimestamp(),
        
        // Creator information
        createdBy: {
          userId: userData?.userId || null,
          fullName: userData?.fullName || '',
          userName: userData?.userName || '',
          profilePictureUrl: userData?.profilePictureUrl || '',
        }
      };
      
      // 1. Add to pendingInvitations subcollection in the event
      const pendingInvitationRef = doc(db, collectionName, selectedEvent.id, 'pendingInvitations', invitationId);
      await setDoc(pendingInvitationRef, invitationData);
      
      // 2. Send notification to the vendor
      await sendEventInvitationNotification(vendor.id, {
        eventId: selectedEvent.id,
        eventType: eventType,
        tourName: selectedEvent.tourName || '',
        showName: eventType === 'show' ? (selectedEvent.showName || selectedEvent.tourName) : '',
        tourId: selectedEvent.id, // For backward compatibility
        invitationId: invitationId,
        actName: selectedEvent.actName || '',
        
        // Date information for this specific invitation
        eventDateId: eventDate.id,
        
        // User information
        userId: vendor.id,
        userName: vendor.userName || '',
        fullName: vendor.fullName || '',
        email: vendor.email || '',
        profilePictureUrl: vendor.profilePictureUrl || '',
        userType: 'Vendor',
        userSubType: vendor.userSubType || '',
        role: 'vendor',
        
        // Creator information
        createdBy: {
          userId: userData?.userId || null,
          fullName: userData?.fullName || '',
          userName: userData?.userName || '',
          profilePictureUrl: userData?.profilePictureUrl || '',
        },
        
        // Additional data
        data: {
          venueDetails: venueData || {}
        }
      });
      
      // Update local state with the new pending vendor
      const vendorDisplay = {
        userId: vendor.id,
        fullName: vendor.fullName || '',
        userName: vendor.userName || '',
        profilePictureUrl: vendor.profilePictureUrl || '',
        userSubType: vendor.userSubType || '',
        email: vendor.email || '',
        idVerificationStatus: vendor.idVerificationStatus || '',
        status: 'pending'
      };
      
      setVendors(prev => [...prev, vendorDisplay]);
      
      // Clear search
      setVendorSearchQuery('');
      setVendorResults([]);
      
      // Close modal
      setIsVendorModalVisible(false);
      
      // Update the event data
      setTimeout(() => {
        updateDate(selectedEvent.id, eventDate.id, eventType);
      }, 1000);
      
    } catch (error) {
      console.error('Error adding vendor:', error);
      alert('Failed to add vendor. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Remove vendor from event
  const handleRemoveVendor = async (vendorId) => {
    if (!window.confirm('Are you sure you want to remove this vendor?')) return;
    
    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // First check if vendor is in assignedUsers
      const vendorRef = doc(db, collectionName, selectedEvent.id, 'assignedUsers', vendorId);
      const vendorDoc = await getDoc(vendorRef);
      
      if (vendorDoc.exists()) {
        const vendorData = vendorDoc.data();
        const assignedDates = vendorData.assignedDates || [];
        
        // Remove this date from assignedDates
        const updatedDates = assignedDates.filter(date => date.dateDocId !== eventDate.id);
        
        if (updatedDates.length === 0) {
          // If no dates left, remove vendor from assignedUsers
          await deleteDoc(vendorRef);
        } else {
          // Update assignedDates
          await updateDoc(vendorRef, {
            assignedDates: updatedDates
          });
        }
      } else {
        // Check if vendor is in pendingInvitations
        const pendingInvitationsQuery = query(
          collection(db, collectionName, selectedEvent.id, 'pendingInvitations'),
          where('userId', '==', vendorId),
          where('status', '==', 'pending')
        );
        
        const pendingSnapshot = await getDocs(pendingInvitationsQuery);
        
        for (const invitationDoc of pendingSnapshot.docs) {
          // Delete the invitation document
          await deleteDoc(invitationDoc.ref);
          
          // Also try to delete from user's eventInvitations collection
          try {
            const userInvitationsQuery = query(
              collection(db, 'users', vendorId, 'eventInvitations'),
              where('eventId', '==', selectedEvent.id),
              where('status', '==', 'pending')
            );
            
            const userInvitationsSnapshot = await getDocs(userInvitationsQuery);
            
            for (const userInvitationDoc of userInvitationsSnapshot.docs) {
              await deleteDoc(userInvitationDoc.ref);
            }
          } catch (userInvError) {
            console.error('Error deleting user invitation:', userInvError);
            // Continue even if this fails
          }
        }
      }
      
      // Update local state
      setVendors(vendors.filter(v => v.userId !== vendorId));
      
      // Update event data
      await updateDate(selectedEvent.id, eventDate.id, eventType);
      
    } catch (error) {
      console.error('Error removing vendor:', error);
      alert('Failed to remove vendor. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Map and ride functions
  const handleViewMap = () => {
    if (venueData.googleMapsUri) {
      window.open(venueData.googleMapsUri, '_blank');
    }
  };

  const handleGetARide = () => {
    setIsRideModalVisible(true);
  };

  const handleOpenCabApp = async (cabService) => {
    if (!venueData || !venueData.location) {
      alert('Venue location data is not available');
      return;
    }

    if (!venueData.location.latitude || !venueData.location.longitude) {
      alert('Invalid venue location data');
      return;
    }

    setIsLocationLoading(true);
    try {
      // Get precise current location
      const position = await getCurrentPosition();
      if (!position) {
        setIsLocationLoading(false);
        return;
      }

      const { latitude: currentLat, longitude: currentLng } = position.coords;
      const venueLat = venueData.location.latitude;
      const venueLng = venueData.location.longitude;
      const venueName = venueData.displayName || 'Venue';
      const venueAddress = venueData.formattedAddress || '';
      const fullVenueAddress = `${venueName}, ${venueAddress}`;

      let url;
      
      if (cabService === 'ola') {
        // OLA URL structure
        url = `https://book.olacabs.com/?pickup_name=Current%20Location&pickup_lat=${currentLat}&pickup_lng=${currentLng}&drop_lat=${venueLat}&drop_lng=${venueLng}&drop_name=${encodeURIComponent(fullVenueAddress)}`;
      } else if (cabService === 'uber') {
        // Uber URL structure - using simple approach
        url = `https://m.uber.com/looking?pickup_lat=${currentLat}&pickup_lng=${currentLng}&dropoff_lat=${venueLat}&dropoff_lng=${venueLng}`;
      } else if (cabService === 'rapido') {
        // For Rapido, we need to get the current address using reverse geocoding
        let currentAddress = "Current Location";
        
        try {
          // Try to get address using reverse geocoding
          const response = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${currentLat}&lon=${currentLng}&format=json`);
          const data = await response.json();
          
          if (data && data.display_name) {
            currentAddress = data.display_name;
          } else {
            // Fallback: use coordinates in the address
            currentAddress = `Location at ${currentLat.toFixed(6)}, ${currentLng.toFixed(6)}`;
          }
        } catch (geocodeError) {
          console.error('Reverse geocoding failed:', geocodeError);
          // Use coordinates as fallback
          currentAddress = `Location at ${currentLat.toFixed(6)}, ${currentLng.toFixed(6)}`;
        }
        
        url = `https://m.rapido.bike/unup-home/seo/${encodeURIComponent(currentAddress)}/${encodeURIComponent(fullVenueAddress)}?version=v3`;
      }

      // Open the URL directly
      window.open(url, '_blank');
      
    } catch (error) {
      console.error('Error getting location:', error);
      alert('Unable to get your current location. Please check your device settings and try again.');
    } finally {
      setIsLocationLoading(false);
      setIsRideModalVisible(false);
    }
  };

  const getCurrentPosition = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        alert('Geolocation is not supported by your browser');
        reject('Geolocation not supported');
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => resolve(position),
        (error) => {
          switch(error.code) {
            case error.PERMISSION_DENIED:
              alert('Location permission denied. Please allow location access and try again.');
              break;
            case error.POSITION_UNAVAILABLE:
              alert('Location information is unavailable. Please check your device settings.');
              break;
            case error.TIMEOUT:
              alert('Location request timed out. Please try again.');
              break;
            default:
              alert('Unable to get your location. Please check your browser settings.');
          }
          reject(error);
        },
        { 
          enableHighAccuracy: true, 
          timeout: 15000, 
          maximumAge: 0 
        }
      );
    });
  };

  const handleCall = () => {
    if (venueData.nationalPhoneNumber) {
      window.location.href = `tel:${venueData.nationalPhoneNumber}`;
    }
  };

  const handleVendorClick = (vendor) => {
    navigate(`/${vendor.userName}`);
  };

  // Navigate to assigned inventory tab
  const handleViewVendorInventory = (vendorId) => {
    if (!selectedEvent?.id || !eventDate?.id) return;
    
    navigate(
      `/${eventType}/${selectedEvent.id}/${eventDate.id}`, 
      { 
        state: { 
          activeTab: 'assignedInventory',
          vendorId: vendorId,
          fromEvent: true
        } 
      }
    );
  };

  const handleViewVenueProfile = () => {
    if (venueData?.id) {
      navigate(`/venue/${venueData.id}`);
    }
  };

  // Vendor Search Modal Component - Fixed to maintain input focus
  const VendorSearchModal = () => {
    if (!isVendorModalVisible) return null;
    
    return (
      <div className={styles.modalOverlay} onClick={() => setIsVendorModalVisible(false)}>
        <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h2>Add Vendor</h2>
            <button className={styles.closeButton} onClick={() => setIsVendorModalVisible(false)}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          <div className={styles.searchBar}>
            <input
              type="text"
              placeholder="Search vendors by name..."
              value={vendorSearchQuery}
              onChange={(e) => setVendorSearchQuery(e.target.value)}
              onKeyDown={handleVendorSearchKeyDown}
              className={styles.searchInput}
              autoFocus
            />
            <button
              className={styles.searchButton}
              onClick={handleVendorSearch}
              disabled={!vendorSearchQuery.trim() || searchingVendors}
            >
              <FontAwesomeIcon icon={faSearch} />
              {searchingVendors ? 'Searching...' : 'Search'}
            </button>
          </div>
          
          <div className={styles.searchResults}>
            {searchingVendors ? (
              <div className={styles.noResults}>Searching...</div>
            ) : vendorResults.length > 0 ? (
              vendorResults.map((vendor) => (
                <div
                  key={vendor.id}
                  className={`${styles.searchResult} ${vendor.isAlreadyAdded ? styles.disabled : ''}`}
                  onClick={() => !vendor.isAlreadyAdded && handleAddVendor(vendor)}
                >
                  <img
                    src={vendor.profilePictureUrl || defaultUserImage}
                    alt={vendor.fullName}
                    className={styles.profilePhoto}
                  />
                  <div className={styles.userInfo}>
                    <div className={styles.nameContainer}>
                      <span className={styles.fullName}>{vendor.fullName}</span>
                      {vendor.idVerificationStatus === 'Verified' && (
                        <img src={verifiedBadge} alt="Verified" className={styles.verifiedBadge} />
                      )}
                    </div>
                    <span className={styles.userName}>@{vendor.userName}</span>
                    <span className={styles.userSubType}>{vendor.userSubType}</span>
                  </div>
                  {vendor.isAlreadyAdded && (
                    <span className={styles.alreadyAddedText}>Already Added</span>
                  )}
                </div>
              ))
            ) : vendorSearchQuery && !searchingVendors ? (
              <div className={styles.noResults}>No vendors found</div>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles['event-container']}>
      {/* Venue Card - Always shown */}
      {venueData.displayName && (
        <div className={styles['event-venue-card']}>
          <div className={styles['event-venue-header']}>
            <img
              src={venueData.placeCoverPhotoUrl || defaultVenueImage}
              alt={venueData.displayName}
              className={styles['event-venue-image']}
            />
            <div className={styles['event-venue-info']}>
              <h3 className={styles['event-venue-name']}>{venueData.displayName}</h3>
              <p className={styles['event-venue-address']}>{venueData.formattedAddress}</p>
              <button 
                className={styles['view-ratings-button']}
                onClick={handleViewVenueProfile}
              >
                <FontAwesomeIcon icon={faEye} />
                <span>View Ratings & Details</span>
              </button>
            </div>
          </div>
          
          <div className={styles['event-actions']}>
            <button className={styles['event-action-button']} onClick={handleViewMap}>
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              <span>View Map</span>
            </button>
            <button className={styles['event-action-button']} onClick={handleGetARide}>
              <FontAwesomeIcon icon={faCar} />
              <span>Get a Ride</span>
            </button>
            <button className={styles['event-action-button']} onClick={handleCall}>
              <FontAwesomeIcon icon={faPhone} />
              <span>Call</span>
            </button>
          </div>
        </div>
      )}
  
      {/* Organizer Section - Only show if not Vendor/Staff */}
      {!isVendorOrStaff && (
        <button 
          className={styles['event-organizer-section']}
          onClick={() => setIsOrganizerModalVisible(true)}
        >
          <div className={styles['event-organizer-info']}>
            <FontAwesomeIcon icon={faUser} className={styles['event-organizer-icon']} />
            <span className={styles['event-organizer-name']}>
              Organizer: {event?.organizer?.organizerName || 'Add Organizer Name'}
            </span>
          </div>
          <FontAwesomeIcon icon={event?.organizer?.organizerName ? faEdit : faChevronRight} />
        </button>
      )}
  
      {/* Navigation Buttons - Conditional based on user type */}
      {!isVendorOrStaff && (
        <>
          <button className={styles['event-nav-button']} onClick={handleNavigateToGuestList}>
            <div className={styles['event-nav-button-content']}>
              <FontAwesomeIcon icon={faUserGroup} className={styles['event-nav-icon']} />
              <span>Guest List</span>
              {eventDate?.event?.guestlist?.length > 0 && (
                <span className={styles['event-button-badge']}>
                  {eventDate.event.guestlist.length}
                </span>
              )}
            </div>
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
      
          <button className={styles['event-nav-button']} onClick={handleNavigateToSetlist}>
            <div className={styles['event-nav-button-content']}>
              <FontAwesomeIcon icon={faMusic} className={styles['event-nav-icon']} />
              <span>Setlist</span>
            </div>
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </>
      )}
  
      {/* Tech Rider button - Always show */}
      <button className={styles['event-nav-button']} onClick={handleNavigateToTechRider}>
        <div className={styles['event-nav-button-content']}>
          <FontAwesomeIcon icon={faFileAlt} className={styles['event-nav-icon']} />
          <span>Tech Rider</span>
          {techRiderCount > 0 && (
            <span className={styles['event-button-badge']}>
              {techRiderCount}
            </span>
          )}
        </div>
        <FontAwesomeIcon icon={faChevronRight} />
      </button>

      <button className={styles['event-nav-button']} onClick={handleNavigateToChecklist}>
        <div className={styles['event-nav-button-content']}>
          <FontAwesomeIcon icon={faClipboardCheck} className={styles['event-nav-icon']} />
          <span>Checklist</span>
        </div>
        <FontAwesomeIcon icon={faChevronRight} />
      </button>
  
      {/* Vendors Section - Always show */}
      <div className={styles['event-vendor-section']}>
        <button 
          className={styles['event-vendor-header']}
          onClick={() => !isVendorOrStaff && setIsVendorModalVisible(true)}
        >
          <div className={styles['event-vendor-header-content']}>
            <FontAwesomeIcon icon={faUsers} className={styles['event-nav-icon']} />
            <span>Vendors</span>
            {vendors.length > 0 && (
              <span className={styles['event-vendor-count']}>({vendors.length})</span>
            )}
          </div>
          {!isVendorOrStaff && <FontAwesomeIcon icon={faPlus} />}
        </button>

        {vendors.length > 0 && (
          <div className={styles['event-vendor-list']}>
            {vendors.map((vendor, index) => (
              <div
                key={vendor.userId || index}
                className={styles['event-vendor-item']}
                onClick={() => handleVendorClick(vendor)}
              >
                <img
                  src={vendor.profilePictureUrl || defaultUserImage}
                  alt={vendor.fullName}
                  className={styles['event-vendor-image']}
                />
                <div className={styles['event-vendor-info']}>
                  <div className={styles['event-vendor-name']}>
                    {vendor.fullName}
                    {vendor.idVerificationStatus === 'Verified' && (
                      <img src={verifiedBadge} alt="Verified" className={styles.verifiedBadge} />
                    )}
                  </div>
                  <div className={styles['event-vendor-username']}>@{vendor.userName}</div>
                  <div className={styles['event-vendor-subtype']}>{vendor.userSubType}</div>
                  {vendor.status === 'pending' && (
                    <span className={styles['vendor-status-pending']}>Pending</span>
                  )}
                  
                  {/* Inventory button inside vendor info section */}
                  <button
                    className={styles['vendor-inventory-button']}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewVendorInventory(vendor.userId);
                    }}
                  >
                    <FontAwesomeIcon icon={faSuitcase} />
                    <span>View Assigned Inventory For This Event</span>
                  </button>
                </div>
                
                {/* Delete button - only show to authorized users */}
                {canRemoveVendor && vendor.status === 'accepted' && (
                  <button
                    className={styles['remove-vendor-button']}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveVendor(vendor.userId);
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <VendorSearchModal />
      
      {isOrganizerModalVisible && (
        <div className={styles['event-modal-overlay']}>
          <div className={styles['event-modal-content']}>
            <div className={styles['event-modal-header']}>
              <h3 className={styles['event-modal-title']}>Enter Organizer Name</h3>
            </div>
            <input
              type="text"
              className={styles['event-modal-input']}
              value={organizerName}
              onChange={(e) => setOrganizerName(e.target.value)}
              placeholder="Organizer name"
            />
            <div className={styles['event-modal-actions']}>
              <button 
                className={styles['event-action-button']}
                onClick={() => setIsOrganizerModalVisible(false)}
              >
                Cancel
              </button>
              <button
                className={styles['event-action-button']}
                onClick={handleSaveOrganizerName}
                disabled={isSaving}
                style={{ backgroundColor: '#E2384D', color: 'white' }}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Ride Modal with Rapido */}
      {isRideModalVisible && (
        <div className={styles['ride-modal-overlay']} onClick={() => setIsRideModalVisible(false)}>
          <div className={styles['ride-modal-content']} onClick={e => e.stopPropagation()}>
            <div className={styles['ride-modal-header']}>
              <h3 className={styles['ride-modal-title']}>Select Cab Service</h3>
            </div>
            <button 
              className={styles['ride-option-button']}
              onClick={() => handleOpenCabApp('ola')}
              disabled={isLocationLoading}
            >
              <img src={olaLogo} alt="OLA" className={styles['ride-option-logo']} />
              <span className={styles['ride-option-text']}>OLA</span>
            </button>
            <button 
              className={styles['ride-option-button']}
              onClick={() => handleOpenCabApp('uber')}
              disabled={isLocationLoading}
            >
              <img src={uberLogo} alt="UBER" className={styles['ride-option-logo']} />
              <span className={styles['ride-option-text']}>UBER</span>
            </button>
            <button 
              className={styles['ride-option-button']}
              onClick={() => handleOpenCabApp('rapido')}
              disabled={isLocationLoading}
            >
              <img src={rapidoLogo} alt="RAPIDO" className={styles['ride-option-logo']} />
              <span className={styles['ride-option-text']}>RAPIDO</span>
            </button>
          </div>
        </div>
      )}
  
      {/* Loading Overlay */}
      {(isLocationLoading || isProcessing) && (
        <div className={styles['loading-overlay']}>
          <div className={styles['loading-spinner']}></div>
          <div className={styles['loading-text']}>
            {isLocationLoading ? 'Getting current location...' : 'Processing...'}
          </div>
        </div>
      )}
    </div>
  );
};

export default Event;